import { useState, useEffect, useMemo } from 'react';

type CustomBreakpoint = 'huge' | 'xxl' | 'lg' | 'md' | 'sm' | 'xs';
type BreakingPointsInfo = {
  breakpoint: CustomBreakpoint;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isDesktopLarge: boolean;
};
type UseBreakingPoints = (initialWidth?: number) => BreakingPointsInfo;

export const useBreakingPoints: UseBreakingPoints = (
  initialWidth = window.innerWidth,
): BreakingPointsInfo => {
  const [windowWidth, setWindowWidth] = useState<number>(initialWidth);
  const breakPoints = {
    xs: parseInt('480px'.replace('px', ''), 10),
    sm: parseInt('640px'.replace('px', ''), 10),
    md: parseInt('768px'.replace('px', ''), 10),
    xl: parseInt('1024px'.replace('px', ''), 10),
    xxl: parseInt('1200px'.replace('px', ''), 10),
    huge: parseInt('1920px'.replace('px', ''), 10),
  };
  const getBreakPoint = (): CustomBreakpoint => {
    if (windowWidth >= breakPoints.xxl) {
      return 'xxl';
    }
    if (windowWidth < breakPoints.xxl && windowWidth >= breakPoints.xl) {
      return 'lg';
    }
    if (windowWidth < breakPoints.xl && windowWidth >= breakPoints.md) {
      return 'md';
    }
    if (windowWidth < breakPoints.md && windowWidth >= breakPoints.sm) {
      return 'sm';
    }

    return 'xs';
  };

  const callback = (): void => setWindowWidth(window.innerWidth);

  const breakpoint = getBreakPoint();

  const isMobile = useMemo(() => breakpoint === 'xs' || breakpoint === 'sm', [breakpoint]);

  const isTablet = useMemo(() => breakpoint === 'md' || breakpoint === 'lg', [breakpoint]);

  const isDesktop = useMemo(() => breakpoint === 'xxl', [breakpoint]);

  const isDesktopLarge = useMemo(
    () => breakpoint === 'xxl' && windowWidth > breakPoints.huge,
    [breakPoints.huge, breakpoint, windowWidth],
  );

  useEffect((): (() => void) => {
    setWindowWidth(initialWidth || window.innerWidth);
    window.addEventListener('resize', callback);
    return (): void => window.removeEventListener('resize', callback);
  }, []); //eslint-disable-line

  return { breakpoint, isMobile, isTablet, isDesktop, isDesktopLarge };
};
